import { Text, SimpleGrid, Box } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';

import getRadioYesNo from 'helpers/getRadioYesNo';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface WaterMitigationSystemProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const WaterMitigationSystem = ({
  dwelling,
  index,
}: WaterMitigationSystemProps) => {
  const dispatch = useAppDispatch();

  const onPrimaryWaterMitigationChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'primary_water_mitigation_type',
        value: {
          ...dwelling.rating_information.primary_water_mitigation_type,
          [field]: value,
        },
      }),
    );
  };

  const onAuxiliaryWaterMitigationChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'auxiliary_water_mitigation_type',
        value: {
          ...dwelling.rating_information.auxiliary_water_mitigation_type,
          [field]: value,
        },
      }),
    );
  };

  const onMainWaterValveChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'main_water_valve_shut_off_type',
        value: {
          ...dwelling.rating_information.main_water_valve_shut_off_type,
          [field]: value,
        },
      }),
    );
  };

  const onMainWaterValveRadioChange = (value: string, field: string) => {
    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'main_water_valve_shut_off_type',
        value: {
          ...dwelling.rating_information.main_water_valve_shut_off_type,
          [field]: value === 'true',
        },
      }),
    );
  };

  const ratingInfo = dwelling.rating_information;

  if (!ratingInfo) return null;

  return (
    <Box mb="20px">
      {/* Primary Water Mitigation */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Primary Water Mitigation
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Sump Pump Type"
          name="primary_water_mitigation_sump_pump_type"
          value={ratingInfo.primary_water_mitigation_type.sump_pump_type || ''}
          onChange={(e: any) =>
            onPrimaryWaterMitigationChange(e, 'sump_pump_type')
          }
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Auxiliary Power"
          name="primary_water_mitigation_auxiliary_power"
          value={ratingInfo.primary_water_mitigation_type.auxiliary_power || ''}
          onChange={(e: any) =>
            onPrimaryWaterMitigationChange(e, 'auxiliary_power')
          }
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Back Up Valve"
          name="primary_water_mitigation_back_up_valve"
          value={ratingInfo.primary_water_mitigation_type.back_up_valve || ''}
          onChange={(e: any) =>
            onPrimaryWaterMitigationChange(e, 'back_up_valve')
          }
          disabled
        />
      </SimpleGrid>

      {/* Auxiliary Water Mitigation */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Auxiliary Water Mitigation
      </Text>
      <SimpleGrid columns={6} spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Sump Pump Type"
          name="auxiliary_water_mitigation_sump_pump_type"
          value={
            ratingInfo.auxiliary_water_mitigation_type.sump_pump_type || ''
          }
          onChange={(e: any) =>
            onAuxiliaryWaterMitigationChange(e, 'sump_pump_type')
          }
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Auxiliary Power"
          name="auxiliary_water_mitigation_auxiliary_power"
          value={
            ratingInfo.auxiliary_water_mitigation_type.auxiliary_power || ''
          }
          onChange={(e: any) =>
            onAuxiliaryWaterMitigationChange(e, 'auxiliary_power')
          }
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Back Up Valve"
          name="auxiliary_water_mitigation_back_up_valve"
          value={ratingInfo.auxiliary_water_mitigation_type.back_up_valve || ''}
          onChange={(e: any) =>
            onAuxiliaryWaterMitigationChange(e, 'back_up_valve')
          }
        />
      </SimpleGrid>

      {/* Main Water Valve Shut Off */}
      <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
        Main Water Valve Shut Off
      </Text>
      <SimpleGrid templateColumns="1fr 1fr 2fr 2fr" spacing="15px" mb="15px">
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Type"
          name="main_water_valve_shut_off_type"
          value={ratingInfo.main_water_valve_shut_off_type.type || ''}
          onChange={(e: any) => onMainWaterValveChange(e, 'type')}
          disabled
        />
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Number of Sensors"
          name="main_water_valve_shut_off_sensors"
          type="number"
          value={
            ratingInfo.main_water_valve_shut_off_type.number_of_main_water_valve_shut_off_sensors.toString() ||
            ''
          }
          onChange={(e: any) =>
            onMainWaterValveChange(
              e,
              'number_of_main_water_valve_shut_off_sensors',
            )
          }
        />
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Sewer Backup Questionnaire Attached"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            ratingInfo.main_water_valve_shut_off_type
              .is_sewer_backup_questionnaire_attached,
          )}
          onChange={(value: string) =>
            onMainWaterValveRadioChange(
              value,
              'is_sewer_backup_questionnaire_attached',
            )
          }
          stacked
        />
      </SimpleGrid>
    </Box>
  );
};

export default WaterMitigationSystem;
